import { BHK_CONFIGS, type BhkConfig } from '@resolute/schema';

/**
 * Free-text parser for the hero search bar.
 *
 * The search bar is a UNIVERSAL search — whatever is typed is matched against
 * the whole inventory, independent of the category strip and the filter bar
 * above it (see `hero-search.tsx`). To make that work, structured intent that
 * the user expresses *in words* has to become a real filter param rather than
 * being passed through as opaque `q` text: CPzone's `search` field matches
 * project/builder/locality names, so "3 BHK" as free text matches nothing,
 * while `config=3` returns every 3 BHK project on the site.
 *
 * Today that means BHK configurations. Everything else stays as residual `q`
 * text, minus the generic filler words ("flats", "in", "show me") that would
 * otherwise be searched as if they were part of a project's name and reduce an
 * otherwise-good query to zero results.
 */

export interface ParsedSearchText {
  /** Residual free text for the `q` param. Undefined when the query was
   *  entirely structured ("3 bhk") or entirely filler ("show me all flats") —
   *  in both cases there is no name to match on, only filters to apply. */
  q?: string;
  /** BHK configurations recognised in the text (→ the `config` param). */
  config: BhkConfig[];
}

/** Numeric value of each config in enum order; the open-ended '5+' counts as 5. */
const CONFIG_NUMBERS: number[] = BHK_CONFIGS.map((c) => (c === '5+' ? 5 : Number(c)));

// A "BHK phrase" is one or more numbers, optionally joined by list/range
// separators, followed by a BHK keyword — "3bhk", "3 BHK", "3-bhk",
// "2 & 3 bhk", "2-3 BHK", "3+ bhk", "4 bedroom".
const NUM = String.raw`\d{1,2}(?:\.\d)?\s*\+?`;
const SEP = String.raw`\s*(?:,|&|-|–|—|\/|\bto\b|\band\b|\bor\b)\s*`;
const KEYWORD = String.raw`(?:bhk|bedrooms?|beds?)`;
const BHK_PHRASE = new RegExp(`\\b${NUM}(?:${SEP}${NUM})*[\\s-]*${KEYWORD}\\b`, 'gi');
/** The pieces of a matched phrase, in order — numbers, '+', and separators. */
const PHRASE_TOKENS = /\d{1,2}(?:\.\d)?|\+|,|&|-|–|—|\/|\bto\b|\band\b|\bor\b/gi;
const RANGE_TOKENS = /^(?:-|–|—|to)$/i;

/**
 * Spoken counts arrive as words — voice dictation transcribes "three BHK" as
 * often as "3 BHK" — so they're folded to digits before matching.
 *
 * Only ever *inside* a BHK phrase (the lookahead below requires the keyword):
 * "One Avighna Park" is a project name, not a 1 BHK, and must survive as typed.
 */
const WORD_NUMBERS: Record<string, string> = {
  one: '1', two: '2', three: '3', four: '4', five: '5',
  six: '6', seven: '7', eight: '8', nine: '9', ten: '10',
};
const WORD = `(?:${Object.keys(WORD_NUMBERS).join('|')})`;
const ANY_NUM = String.raw`(?:\d{1,2}(?:\.\d)?|${WORD})\s*\+?`;
const WORD_IN_PHRASE = new RegExp(
  `\\b(${WORD})\\b(?=(?:${SEP}${ANY_NUM})*[\\s-]*${KEYWORD}\\b)`,
  'gi',
);

/**
 * Words that are never part of a project, builder or locality name — asking
 * CPzone to name-match them is what makes an otherwise-good query ("3 bhk
 * flats in satellite") return nothing. Stripped wherever they appear.
 */
const FILLER = new Set([
  // connectors / phrasing
  'in', 'at', 'near', 'nearby', 'around', 'on', 'of', 'for', 'with', 'to', 'and', 'or',
  'a', 'an', 'the', 'any', 'all', 'show', 'me', 'my', 'i', 'want', 'need', 'looking',
  'please', 'available', 'under', 'buy', 'sale', 'rent',
  // generic inventory nouns
  'bhk', 'flat', 'flats', 'property', 'properties', 'project', 'projects',
  'unit', 'units', 'home', 'homes', 'house', 'houses',
]);

/**
 * Nearest config at or below `n` — so an off-vocabulary value ("2.7 BHK", or a
 * 6 BHK that the inventory buckets as '5+') still filters instead of being
 * silently dropped.
 */
function toConfig(raw: string): BhkConfig | undefined {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return undefined;
  if (n >= 5) return '5+';
  let idx = 0;
  for (let i = 0; i < BHK_CONFIGS.length; i += 1) {
    if (CONFIG_NUMBERS[i]! <= n) idx = i;
  }
  return BHK_CONFIGS[idx];
}

/** Inclusive slice of the config vocabulary between two configs. */
const between = (a: BhkConfig, b: BhkConfig): BhkConfig[] => {
  const [lo, hi] = [BHK_CONFIGS.indexOf(a), BHK_CONFIGS.indexOf(b)].sort((x, y) => x - y) as [
    number,
    number,
  ];
  return BHK_CONFIGS.slice(lo, hi + 1);
};

/** Reads the configs out of a single matched BHK phrase. */
function configsFromPhrase(phrase: string): BhkConfig[] {
  const found: BhkConfig[] = [];
  let previous: BhkConfig | undefined;
  let openRange = false;

  for (const token of phrase.match(PHRASE_TOKENS) ?? []) {
    if (/^\d/.test(token)) {
      const config = toConfig(token);
      if (!config) continue;
      // "2-3 bhk" spans the vocabulary between the two ends (a 2.5 BHK *is*
      // between 2 and 3); a plain list ("2 & 3") takes only what was named.
      found.push(...(openRange && previous ? between(previous, config) : [config]));
      previous = config;
      openRange = false;
    } else if (RANGE_TOKENS.test(token)) {
      openRange = true;
    } else if (token === '+') {
      // "3+ bhk" — the named config and everything above it.
      if (previous) found.push(...BHK_CONFIGS.slice(BHK_CONFIGS.indexOf(previous)));
      openRange = false;
    } else {
      openRange = false;
    }
  }

  return found;
}

/** Parses a raw hero-search query into the search params it implies. */
export function parseSearchText(raw: string): ParsedSearchText {
  const text = raw.trim().replace(WORD_IN_PHRASE, (word) => WORD_NUMBERS[word.toLowerCase()]!);
  if (!text) return { config: [] };

  const configs = new Set<BhkConfig>();
  for (const phrase of text.match(BHK_PHRASE) ?? []) {
    for (const config of configsFromPhrase(phrase)) configs.add(config);
  }

  const residual = text
    .replace(BHK_PHRASE, ' ')
    // Sentence punctuation — dictation ends on a full stop ("…flats in Bopal.")
    // and a trailing period would be name-matched. Only stripped at a word
    // boundary, so the decimal in "2.5" is untouched.
    .replace(/[.!?]+(?=\s|$)/g, ' ')
    .replace(/[,;|"“”]+/g, ' ')
    .split(/\s+/)
    .filter((word) => word && !FILLER.has(word.toLowerCase()))
    .join(' ')
    .trim();

  return {
    // Keep the vocabulary's own order so the value is stable regardless of how
    // the user happened to phrase the range.
    config: BHK_CONFIGS.filter((c) => configs.has(c)),
    ...(residual ? { q: residual } : {}),
  };
}
